import { Box, Typography } from '@mui/material';
import { memo } from 'react';
import type {
  SyberryTodayTaskId,
  SyberryTodayTaskStream,
  SyberryTodayWorkItem,
} from 'domain/people/syberryToday/types';
import SyberryTodayCountBadge, { SyberryTodayCountBadgeSize } from '../SyberryTodayCountBadge/SyberryTodayCountBadge';
import SyberryTodayTaskCard from '../SyberryTodayTaskCard/SyberryTodayTaskCard';

type SyberryTodayBoardColumnProps = {
  stream: SyberryTodayTaskStream;
  tasks: SyberryTodayWorkItem[];
  expandedTaskId: Nullable<SyberryTodayTaskId>;
  onToggleSelection: (taskId: SyberryTodayTaskId) => void;
  onToggleExpansion: (taskId: SyberryTodayTaskId) => void;
};

const areTasksEqual = (previousTasks: SyberryTodayWorkItem[], nextTasks: SyberryTodayWorkItem[]) =>
  previousTasks.length === nextTasks.length && previousTasks.every((task, index) => task === nextTasks[index]);

const getExpandedTaskIdInColumn = (
  tasks: SyberryTodayWorkItem[],
  expandedTaskId: Nullable<SyberryTodayTaskId>,
): Nullable<SyberryTodayTaskId> => {
  if (!expandedTaskId) return null;

  return tasks.some(task => task.id === expandedTaskId) ? expandedTaskId : null;
};

const SyberryTodayBoardColumn = ({
  stream,
  tasks,
  expandedTaskId,
  onToggleSelection,
  onToggleExpansion,
}: SyberryTodayBoardColumnProps) => (
  <Box component="section" sx={{ minWidth: 0 }} aria-label={`${stream.name} tasks`}>
    <Box display="flex" alignItems="center" gap={1.25} marginBottom={3}>
      <Typography component="h2" variant="h2" color="primary">
        {stream.name}
      </Typography>
      <SyberryTodayCountBadge
        value={tasks.length}
        aria-label={`${tasks.length} tasks in ${stream.name}`}
        size={SyberryTodayCountBadgeSize.SMALL}
      />
    </Box>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {tasks.map(task => (
        <SyberryTodayTaskCard
          key={task.id}
          task={task}
          isExpanded={expandedTaskId === task.id}
          onToggleSelection={onToggleSelection}
          onToggleExpansion={onToggleExpansion}
        />
      ))}
    </Box>
  </Box>
);

export default memo(
  SyberryTodayBoardColumn,
  (previousProps, nextProps) =>
    previousProps.stream === nextProps.stream &&
    previousProps.onToggleSelection === nextProps.onToggleSelection &&
    previousProps.onToggleExpansion === nextProps.onToggleExpansion &&
    areTasksEqual(previousProps.tasks, nextProps.tasks) &&
    getExpandedTaskIdInColumn(previousProps.tasks, previousProps.expandedTaskId) ===
      getExpandedTaskIdInColumn(nextProps.tasks, nextProps.expandedTaskId),
);
